import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { api } from '../../services/api';

const ESPN_LOGO_URL = 'https://a.espncdn.com/i/teamlogos/nba/500';

// Team abbreviation mapping for ESPN logos
const TEAM_ABBR_MAP = {
  'Philadelphia 76ers': 'phi',
  '76ers': 'phi',
  'Dallas Mavericks': 'dal',
  'Mavericks': 'dal',
  'Memphis Grizzlies': 'mem',
  'Grizzlies': 'mem',
  'Los Angeles Lakers': 'lal',
  'Lakers': 'lal',
  'Boston Celtics': 'bos',
  'Celtics': 'bos',
  'Golden State Warriors': 'gsw',
  'Warriors': 'gsw',
  'Miami Heat': 'mia',
  'Heat': 'mia',
  'Phoenix Suns': 'phx',
  'Suns': 'phx',
  'Denver Nuggets': 'den',
  'Nuggets': 'den',
  'Milwaukee Bucks': 'mil',
  'Bucks': 'mil',
  'Cleveland Cavaliers': 'cle',
  'Cavaliers': 'cle',
  'Oklahoma City Thunder': 'okc',
  'Thunder': 'okc',
  'New York Knicks': 'ny',
  'Knicks': 'ny',
  'Brooklyn Nets': 'bkn',
  'Nets': 'bkn',
  'Toronto Raptors': 'tor',
  'Raptors': 'tor',
  'Chicago Bulls': 'chi',
  'Bulls': 'chi',
  'Indiana Pacers': 'ind',
  'Pacers': 'ind',
  'Detroit Pistons': 'det',
  'Pistons': 'det',
  'Atlanta Hawks': 'atl',
  'Hawks': 'atl',
  'Charlotte Hornets': 'cha',
  'Hornets': 'cha',
  'Washington Wizards': 'wsh',
  'Wizards': 'wsh',
  'Orlando Magic': 'orl',
  'Magic': 'orl',
  'San Antonio Spurs': 'sa',
  'Spurs': 'sa',
  'Houston Rockets': 'hou',
  'Rockets': 'hou',
  'New Orleans Pelicans': 'no',
  'Pelicans': 'no',
  'Minnesota Timberwolves': 'min',
  'Timberwolves': 'min',
  'Utah Jazz': 'utah',
  'Jazz': 'utah',
  'Portland Trail Blazers': 'por',
  'Trail Blazers': 'por',
  'Sacramento Kings': 'sac',
  'Kings': 'sac',
  'Los Angeles Clippers': 'lac',
  'Clippers': 'lac',
};

// Polling interval in milliseconds (30 seconds)
const POLLING_INTERVAL = 30000;

/**
 * LiveScorePanel - Displays real-time game score, period, time, and win probabilities
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6
 */
export function LiveScorePanel({ eventId, market, isLive }) {
  const { t } = useTranslation();
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const getTeamLogo = (team) => {
    if (!team) return '';
    const abbr = TEAM_ABBR_MAP[team] || team.toLowerCase().split(' ').pop();
    return `${ESPN_LOGO_URL}/${abbr}.png`;
  };

  const fetchLiveScore = useCallback(async () => {
    if (!eventId || !isLive) return;
    
    try {
      const data = await api.getLiveScore(eventId);
      setLiveData(data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.message || t('live.dataUnavailable'));
    } finally {
      setLoading(false);
    }
  }, [eventId, isLive, t]);

  // Initial fetch and polling setup
  useEffect(() => {
    if (!isLive) {
      setLoading(false);
      return;
    }

    fetchLiveScore();

    // Set up polling every 30 seconds (Requirement 1.5)
    const intervalId = setInterval(fetchLiveScore, POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, [fetchLiveScore, isLive]);

  // Don't render if not live
  if (!isLive) return null;

  // Loading state
  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-pulse text-gray-400">{t('common.loading')}</div>
        </div>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center text-gray-400">
          {t('live.dataUnavailable')}
        </div>
      </Card>
    );
  }

  // Use live data or fallback to market data
  const homeScore = liveData?.homeScore ?? 0;
  const awayScore = liveData?.awayScore ?? 0;
  const period = liveData?.period ?? 1;
  const clock = liveData?.clock ?? '';
  const statusDetail = liveData?.statusDetail ?? '';
  const homeWinProb = liveData?.homeWinProb ?? market?.priceB ?? 0.5;
  const awayWinProb = liveData?.awayWinProb ?? market?.priceA ?? 0.5;
  const probChange = liveData?.probChange ?? 0;
  const homeTeamName = liveData?.homeTeamName ?? market?.teamB ?? '';
  const awayTeamName = liveData?.awayTeamName ?? market?.teamA ?? '';

  // Format probability change indicator
  const formatProbChange = (change) => {
    if (change === 0) return '→0.0%';
    const sign = change > 0 ? '↑' : '↓';
    return `${sign}${Math.abs(change * 100).toFixed(1)}%`;
  };

  return (
    <Card className="p-6">
      {/* Header with LIVE badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Badge variant="positive" className="animate-pulse">
            🔴 LIVE
          </Badge>
          <span className="text-sm text-gray-400">
            {statusDetail || `Q${period}`}
          </span>
          {clock && (
            <span className="text-sm text-white font-mono">
              {clock}
            </span>
          )}
        </div>
        {lastUpdate && (
          <span className="text-xs text-gray-500">
            {t('live.lastUpdate')}: {lastUpdate.toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* Main score display */}
      <div className="flex items-center justify-between">
        {/* Away Team (Team A) */}
        <div className="flex-1 text-center">
          <img
            src={getTeamLogo(awayTeamName)}
            alt={awayTeamName}
            className="w-16 h-16 mx-auto mb-2 object-contain"
            onError={(e) => { e.target.style.opacity = '0.3'; }}
          />
          <div className="text-sm text-gray-400 mb-1">{awayTeamName}</div>
          <div className="text-4xl font-bold text-white">{awayScore}</div>
          <div className="mt-2">
            <span className="text-sm text-accent">
              {(awayWinProb * 100).toFixed(1)}%
            </span>
          </div>
        </div>

        {/* Center - Period/Time and VS */}
        <div className="flex-1 text-center px-4">
          <div className="text-2xl font-bold text-gray-500 mb-2">VS</div>
          <div className="bg-surface-highlight/70 rounded-lg px-4 py-2">
            <div className="text-lg font-bold text-white">
              {statusDetail || `Q${period}`}
            </div>
            {clock && (
              <div className="text-sm text-accent-warm font-mono">
                {clock}
              </div>
            )}
          </div>
          {/* Win probability change indicator (Requirement 2.6) */}
          <div className="mt-2 text-sm">
            <span className={`${probChange >= 0 ? 'text-positive' : 'text-accent-warm'}`}>
              {formatProbChange(probChange)}
            </span>
          </div>
        </div>

        {/* Home Team (Team B) */}
        <div className="flex-1 text-center">
          <img
            src={getTeamLogo(homeTeamName)}
            alt={homeTeamName}
            className="w-16 h-16 mx-auto mb-2 object-contain"
            onError={(e) => { e.target.style.opacity = '0.3'; }}
          />
          <div className="text-sm text-gray-400 mb-1">{homeTeamName}</div>
          <div className="text-4xl font-bold text-white">{homeScore}</div>
          <div className="mt-2">
            <span className="text-sm text-accent-warm">
              {(homeWinProb * 100).toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      {/* Win probability bar */}
      <div className="mt-4">
        <div className="flex justify-between text-xs text-gray-400 mb-1">
          <span>{t('live.winProbability')}</span>
        </div>
        <div className="h-2 bg-surface-highlight/70 rounded-full overflow-hidden flex">
          <div 
            className="bg-accent transition-all duration-500"
            style={{ width: `${awayWinProb * 100}%` }}
          />
          <div 
            className="bg-accent-warm transition-all duration-500"
            style={{ width: `${homeWinProb * 100}%` }}
          />
        </div>
        <div className="flex justify-between text-xs mt-1">
          <span className="text-accent">{awayTeamName}</span>
          <span className="text-accent-warm">{homeTeamName}</span>
        </div>
      </div>
    </Card>
  );
}
